import React, { useState, useEffect } from "react";
import {
	XMarkIcon,
	ChevronUpIcon,
	ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { useEventStore } from "../../../store/eventStore";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// 예약 상세 정보 모달 컴포넌트
const ReservationDetailModal = ({ isOpen, onClose, reservationData }) => {
	const { getReservationDetails, cancelSelectedSeats } = useEventStore();
	const [detailData, setDetailData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [selectedSeats, setSelectedSeats] = useState([]);
	const [selectedSeatInfoIds, setSelectedSeatInfoIds] = useState([]);
	const [allOutboundSelected, setAllOutboundSelected] = useState(false);
	const [allInboundSelected, setAllInboundSelected] = useState(false);
	const [sortConfig, setSortConfig] = useState({
		key: null,
		direction: "ascending",
	});

	// 모달 열릴 때 데이터 불러오기, 닫힐 때 상태 초기화
	useEffect(() => {
		if (isOpen && reservationData) {
			setLoading(true);
			getReservationDetails(reservationData.user_id)
				.then((data) => {
					setDetailData(data);
				})
				.catch((error) => {
					console.error("Error fetching reservation details:", error);
					toast.error(
						"예약 상세 정보를 불러오는 중 오류가 발생했습니다."
					);
				})
				.finally(() => {
					setLoading(false);
				});
		} else {
			// 모달이 닫힐 때 모든 상태 초기화
			setDetailData(null);
			setSelectedSeats([]);
			setSelectedSeatInfoIds([]);
			setAllOutboundSelected(false);
			setAllInboundSelected(false);
		}
	}, [isOpen, reservationData, getReservationDetails]);

	// 모달 닫기 핸들러
	const handleClose = () => {
		// 모달을 닫기 전에 상태 초기화
		setSelectedSeats([]);
		setSelectedSeatInfoIds([]);
		setAllOutboundSelected(false);
		setAllInboundSelected(false);
		onClose();
	};

	// 개별 좌석 선택 처리
	const handleSeatSelect = (seatId, seatInfoId) => {
		// 좌석 ID 관리 (화면 표시용)
		setSelectedSeats((prevSelected) => {
			if (prevSelected.includes(seatId)) {
				return prevSelected.filter((id) => id !== seatId);
			} else {
				return [...prevSelected, seatId];
			}
		});

		// seat_info_id 관리 (API 요청용)
		setSelectedSeatInfoIds((prevSelected) => {
			if (prevSelected.includes(seatInfoId)) {
				return prevSelected.filter((id) => id !== seatInfoId);
			} else {
				return [...prevSelected, seatInfoId];
			}
		});
	};

	// 왕편 전체 선택/해제
	const handleToggleAllOutbound = () => {
		const newAllOutboundSelected = !allOutboundSelected;
		setAllOutboundSelected(newAllOutboundSelected);

		if (newAllOutboundSelected) {
			// 전체 선택
			const outboundTickets =
				detailData?.filter(
					(item) => parseInt(item.train_code) % 2 === 1
				) || [];
			const outboundTicketIds = outboundTickets.flatMap((ticket) =>
				ticket.seat_info_list.map((seatInfo) => seatInfo.seat_info_id)
			);
			setSelectedSeatInfoIds((prev) => [
				...prev,
				...outboundTicketIds.filter((id) => !prev.includes(id)),
			]);
		} else {
			// 전체 해제
			const outboundTickets =
				detailData?.filter(
					(item) => parseInt(item.train_code) % 2 === 1
				) || [];
			const outboundTicketIds = outboundTickets.flatMap((ticket) =>
				ticket.seat_info_list.map((seatInfo) => seatInfo.seat_info_id)
			);
			setSelectedSeatInfoIds((prev) =>
				prev.filter((id) => !outboundTicketIds.includes(id))
			);
		}
	};

	// 복편 전체 선택/해제 처리
	const handleToggleAllInbound = () => {
		const newAllInboundSelected = !allInboundSelected;
		setAllInboundSelected(newAllInboundSelected);

		if (newAllInboundSelected) {
			// 전체 선택
			const inboundTickets =
				detailData?.filter(
					(item) => parseInt(item.train_code) % 2 === 0
				) || [];
			const inboundTicketIds = inboundTickets.flatMap((ticket) =>
				ticket.seat_info_list.map((seatInfo) => seatInfo.seat_info_id)
			);
			setSelectedSeatInfoIds((prev) => [
				...prev,
				...inboundTicketIds.filter((id) => !prev.includes(id)),
			]);
		} else {
			// 전체 해제
			const inboundTickets =
				detailData?.filter(
					(item) => parseInt(item.train_code) % 2 === 0
				) || [];
			const inboundTicketIds = inboundTickets.flatMap((ticket) =>
				ticket.seat_info_list.map((seatInfo) => seatInfo.seat_info_id)
			);
			setSelectedSeatInfoIds((prev) =>
				prev.filter((id) => !inboundTicketIds.includes(id))
			);
		}
	};

	// 좌석 배정 취소 처리
	const handleCancelSeats = async () => {
		if (selectedSeatInfoIds.length === 0) {
			toast.warning("취소할 좌석을 선택해주세요.");
			return;
		}

		// 사용자 확인
		if (
			window.confirm(
				`선택한 ${selectedSeatInfoIds.length}개 좌석의 배정을 취소하시겠습니까?`
			)
		) {
			try {
				const result = await cancelSelectedSeats(
					reservationData.user_id,
					selectedSeatInfoIds
				);

				if (result.success) {
					toast.success("좌석 배정이 취소되었습니다.");

					// 데이터 새로고침
					setLoading(true);
					getReservationDetails(reservationData.user_id)
						.then((data) => {
							setDetailData(data);
							// 선택 상태 초기화
							setSelectedSeats([]);
							setAllOutboundSelected(false);
							setAllInboundSelected(false);
						})
						.catch((error) => {
							console.error(
								"Error refreshing reservation details:",
								error
							);
						})
						.finally(() => {
							setLoading(false);
						});
				} else {
					toast.error(
						result.error || "좌석 배정 취소에 실패했습니다."
					);
				}
			} catch (error) {
				console.error("Error canceling seats:", error);
				toast.error("좌석 배정 취소 중 오류가 발생했습니다.");
			}
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
				<div
					className="fixed inset-0 transition-opacity"
					aria-hidden="true"
				>
					<div
						className="absolute inset-0 bg-gray-500 opacity-75"
						onClick={onClose}
					></div>
				</div>

				<span
					className="hidden sm:inline-block sm:align-middle sm:h-screen"
					aria-hidden="true"
				>
					&#8203;
				</span>

				<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
					{/* 헤더 */}
					<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
						<div className="flex justify-between items-center pb-3 border-b mb-4">
							<div>
								<h3 className="text-lg font-semibold text-gray-900">
									예약 상세 정보
								</h3>
							</div>
							<button
								onClick={handleClose}
								className="text-gray-400 hover:text-gray-500 focus:outline-none"
							>
								<XMarkIcon className="h-6 w-6" />
							</button>
						</div>

						{loading ? (
							<div className="flex flex-col justify-center items-center h-64">
								<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
								<p className="text-gray-600 text-sm">
									데이터를 불러오는 중입니다...
								</p>
							</div>
						) : (
							<div>
								{/* 예약자 정보 */}
								<div className="mb-6">
									<h4 className="text-base font-medium mb-2">
										예약자 정보
									</h4>
									<div className="bg-gray-50 p-4 rounded-md">
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
											<div>
												<p className="text-sm text-gray-500">
													예약자명
												</p>
												<p className="text-sm font-medium">
													{detailData?.[0]?.seat_info_list?.[0]?.user_name || "-"}
												</p>
											</div>
											<div>
												<p className="text-sm text-gray-500">
													연락처
												</p>
												<p className="text-sm font-medium">
													{detailData?.[0]?.seat_info_list?.[0]?.user_phone || "-"}
												</p>
											</div>
											<div>
												<p className="text-sm text-gray-500">
													인원 수
												</p>
												<p className="text-sm font-medium">
													{detailData?.[0]?.seat_info_list?.[0]?.user_reservation_cnt || "-"}
												</p>
											</div>
											<div>
												<p className="text-sm text-gray-500">
													예약 번호
												</p>
												<p className="text-sm font-medium">
													{detailData?.[0]?.reservation_number || "-"}
												</p>
											</div>
										</div>
									</div>
								</div>

								{/* 배정된 승차권 정보 테이블 */}
								<div className="mb-6">
									<h4 className="text-base font-medium mb-2">
										배정된 승차권
									</h4>

									{/* 왕편 승차권 */}
									<div className="mb-4">
										<h5 className="text-sm font-medium mb-2">
											왕편 승차권
										</h5>
										<div className="overflow-x-auto">
											<table className="min-w-full divide-y divide-gray-200">
												<thead className="bg-blue-50">
													<tr>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															<input
																type="checkbox"
																className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
																onChange={
																	handleToggleAllOutbound
																}
																checked={
																	allOutboundSelected
																}
															/>
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															열차 번호
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															출발역
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															도착역
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															출발 날짜
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															출발 시간
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															도착 시간
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															예약 번호
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															좌석 정보
														</th>
													</tr>
												</thead>
												<tbody className="bg-white divide-y divide-gray-200">
													{detailData?.filter(
														(item) =>
															parseInt(
																item.train_code
															) %
																2 ===
															1
													).length > 0 ? (
														detailData
															?.filter(
																(item) =>
																	parseInt(
																		item.train_code
																	) %
																		2 ===
																	1
															)
															.flatMap(
																(
																	ticket,
																	ticketIndex
																) => {
																	return ticket.seat_info_list.map(
																		(
																			seatInfo,
																			seatIndex
																		) => {
																			const ticketId = `${ticket.train_code}-${seatInfo.seat_code}`;
																			return (
																				<tr
																					key={`outbound-${ticketIndex}-${seatIndex}`}
																				>
																					<td className="px-3 py-4 whitespace-nowrap">
																						<input
																							type="checkbox"
																							className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
																							onChange={() =>
																								handleSeatSelect(
																									ticketId,
																									seatInfo.seat_info_id
																								)
																							}
																							checked={
																								selectedSeatInfoIds.includes(
																									seatInfo.seat_info_id
																								) ||
																								selectedSeats.includes(
																									ticketId,
																									seatInfo.seat_info_id
																								)
																							}
																						/>
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.train_code
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.departure_station
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.arrival_station
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.departure_date
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.departure_time
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.arrival_time
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.reservation_number
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							seatInfo.seat_code
																						}
																					</td>
																				</tr>
																			);
																		}
																	);
																}
															)
													) : (
														<tr>
															<td
																colSpan="9"
																className="px-3 py-4 text-center text-sm text-gray-500"
															>
																배정된 왕편
																승차권이
																없습니다.
															</td>
														</tr>
													)}
												</tbody>
											</table>
										</div>
									</div>

									{/* 복편 승차권 */}
									<div>
										<h5 className="text-sm font-medium mb-2">
											복편 승차권
										</h5>
										<div className="overflow-x-auto">
											<table className="min-w-full divide-y divide-gray-200">
												<thead className="bg-blue-50">
													<tr>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															<input
																type="checkbox"
																className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
																onChange={
																	handleToggleAllInbound
																}
																checked={
																	allInboundSelected
																}
															/>
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															열차 번호
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															출발역
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															도착역
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															출발 날짜
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															출발 시간
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															도착 시간
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															예약 번호
														</th>
														<th
															scope="col"
															className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
														>
															좌석 정보
														</th>
													</tr>
												</thead>
												<tbody className="bg-white divide-y divide-gray-200">
													{detailData?.filter(
														(item) =>
															parseInt(
																item.train_code
															) %
																2 ===
															0
													).length > 0 ? (
														detailData
															?.filter(
																(item) =>
																	parseInt(
																		item.train_code
																	) %
																		2 ===
																	0
															)
															.flatMap(
																(
																	ticket,
																	ticketIndex
																) => {
																	return ticket.seat_info_list.map(
																		(
																			seatInfo,
																			seatIndex
																		) => {
																			const ticketId = `${ticket.train_code}-${seatInfo.seat_code}`;
																			return (
																				<tr
																					key={`inbound-${ticketIndex}-${seatIndex}`}
																				>
																					<td className="px-3 py-4 whitespace-nowrap">
																						<input
																							type="checkbox"
																							className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
																							onChange={() =>
																								handleSeatSelect(
																									ticketId,
																									seatInfo.seat_info_id
																								)
																							}
																							checked={
																								selectedSeatInfoIds.includes(
																									seatInfo.seat_info_id
																								) ||
																								selectedSeats.includes(
																									ticketId,
																									seatInfo.seat_info_id
																								)
																							}
																						/>
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.train_code
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.departure_station
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.arrival_station
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.departure_date
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.departure_time
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.arrival_time
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							ticket.reservation_number
																						}
																					</td>
																					<td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
																						{
																							seatInfo.seat_code
																						}
																					</td>
																				</tr>
																			);
																		}
																	);
																}
															)
													) : (
														<tr>
															<td
																colSpan="9"
																className="px-3 py-4 text-center text-sm text-gray-500"
															>
																배정된 복편
																승차권이
																없습니다.
															</td>
														</tr>
													)}
												</tbody>
											</table>
										</div>
									</div>
								</div>
							</div>
						)}
					</div>

					{/* 푸터 */}
					<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
						<button
							type="button"
							className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
							onClick={onClose}
						>
							닫기
						</button>
						<button
							type="button"
							className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
							onClick={handleCancelSeats}
							disabled={selectedSeatInfoIds.length === 0}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								className="h-5 w-5 mr-2"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fillRule="evenodd"
									d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
									clipRule="evenodd"
								/>
							</svg>
							선택한 좌석 배정 취소 ({selectedSeatInfoIds.length})
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ReservationDetailModal;
